import ConsentService from '../Service/ConsentService'
import { getElements } from '../Utility/Dom'
import { IS_HIDDEN } from '../Utility/State'

class Consent {
  private buttons: NodeListOf<HTMLElement>
  private buttonsAcceptOnce: NodeListOf<HTMLElement>
  private readonly consentAcceptEvent: Event
  private readonly consentDeclinedEvent: Event
  private consentItems: NodeListOf<HTMLElement>
  private consentTypes: string[] = ['openstreetmap']
  private eventNameAcceptConsent: string = 'consentAccepted'
  private eventNameDeclineConsent: string = 'consentDeclined'
  private processedConsents: Map<string, boolean> = new Map()

  constructor() {
    this.consentItems = getElements('consent')
    this.buttons = getElements('consent-button')
    this.buttonsAcceptOnce = getElements('consent-accept-once-button')
    this.consentAcceptEvent = new Event(this.eventNameAcceptConsent)
    this.consentDeclinedEvent = new Event(this.eventNameDeclineConsent)
  }

  public init(): void {
    this.addEvents()
    this.handleAcceptedConsents()
  }

  /**
   * Public method to refresh consent items and check accepted consents
   * Useful for dynamically added consent layers (e.g., from Svelte components)
   */
  public refresh(): void {
    this.consentItems = getElements('consent')
    this.buttons = getElements('consent-button')
    this.buttonsAcceptOnce = getElements('consent-accept-once-button')
    this.attachButtonEvents()

    // Unlike handleAcceptedConsents(), this re-dispatches for already-processed types so newly
    // added elements are served too (LocationMap handles duplicate calls).
    for (const type of this.acceptedTypes()) {
      this.enable(type)
      this.processedConsents.set(type, true)
    }
  }

  private acceptedTypes(): string[] {
    return this.consentTypes.filter((type: string): boolean => ConsentService.hasConsent(type))
  }

  private addEvents(): void {
    ConsentService.onChange((): void => {
      this.handleAcceptedConsents()
    })

    this.attachButtonEvents()
  }

  private attachButtonEvents(): void {
    this.buttons.forEach((button) => {
      // Avoid duplicate listeners by checking if already attached
      if (!button.dataset.consentListenerAttached) {
        button.dataset.consentListenerAttached = 'true'
        button.addEventListener('click', () => {
          ConsentService.openWidget()
        })
      }
    })

    this.buttonsAcceptOnce.forEach((button) => {
      // Avoid duplicate listeners by checking if already attached
      if (!button.dataset.consentOnceListenerAttached) {
        button.dataset.consentOnceListenerAttached = 'true'
        button.addEventListener('click', () => {
          this.enable(button.dataset.type)
        })
      }
    })
  }

  private disable(type: string): void {
    this.showConsent(type)

    switch (type) {
      case 'openstreetmap': {
        const mapItems = getElements('map')

        mapItems.forEach((item): void => {
          if (item.dataset.type === type) {
            item.dispatchEvent(this.consentDeclinedEvent)
          }
        })

        break
      }
    }
  }

  private disableScripts(type: string): void {
    const scripts = getElements('consent-required-script')

    scripts.forEach((script: HTMLScriptElement): void => {
      if (script.type === 'text/javascript' && script.dataset.type === type) {
        script.type = 'text/plain'
      }
    })
  }

  private enable(type: string): void {
    this.hideConsent(type)

    switch (type) {
      case 'openstreetmap': {
        const mapItems = getElements('map')

        mapItems.forEach((item): void => {
          if (item.dataset.type === type) {
            item.dispatchEvent(this.consentAcceptEvent)
          }
        })

        break
      }
    }
  }

  /**
   * usage:
   *
   *  <f:asset.script
   *    identifier="GoogleTagManager"
   *    type="text/plain"
   *    nonce="{f:security.nonce()}"
   *    data-js="consent-required-script"
   *    data-type="google tag manager"
   *  >
   *    console.log('Google Tag Manager');
   *  </f:asset.script>
   */
  private enableScripts(type: string): void {
    const scripts = getElements('consent-required-script')

    scripts.forEach((script: HTMLScriptElement): void => {
      if (script.type === 'text/plain' && script.dataset.type === type) {
        const newScript = document.createElement('script')
        const nonceValue = script.nonce || script.getAttribute('nonce')

        Array.from(script.attributes).forEach((attr): void => {
          if (attr.name !== 'type' && attr.name !== 'nonce') {
            newScript.setAttribute(attr.name, attr.value)
          }
        })

        if (nonceValue) {
          newScript.nonce = nonceValue
        }

        newScript.type = 'text/javascript'

        if (script.src) {
          newScript.src = script.src
        } else {
          newScript.textContent = script.textContent
        }

        script.parentNode?.replaceChild(newScript, script)
      }
    })
  }

  private handleAcceptedConsents(): void {
    // Refresh consent items to handle dynamically added elements (e.g., from Svelte components)
    this.consentItems = getElements('consent')
    this.buttons = getElements('consent-button')
    this.buttonsAcceptOnce = getElements('consent-accept-once-button')

    const acceptedTypes = new Set(this.acceptedTypes())

    acceptedTypes.forEach((type: string): void => {
      // Always hide consent layer (important for dynamically re-rendered elements)
      this.hideConsent(type)

      // Only dispatch event once per consent type
      if (this.processedConsents.has(type) === false) {
        this.processedConsents.set(type, true)
        this.enable(type)
      }
    })

    // Handle declined consents (only once per type)
    this.consentTypes.forEach((type: string): void => {
      if (
        acceptedTypes.has(type) === false &&
        this.processedConsents.has(type) === true
      ) {
        this.processedConsents.delete(type)
        this.disable(type)
      }
    })
  }

  private hideConsent(type: string): void {
    this.consentItems.forEach((consentItem) => {
      if (consentItem.dataset.type === type) {
        consentItem.classList.add(IS_HIDDEN)
      }
    })
  }

  private showConsent(type: string): void {
    this.consentItems.forEach((consentItem) => {
      if (consentItem.dataset.type === type) {
        consentItem.classList.remove(IS_HIDDEN)
      }
    })
  }
}

const consent = new Consent()
consent.init()

export default consent
